import { SiteCountry } from './models/locale'

export function validPostcode(val: string, country: SiteCountry) {
  switch (country) {
    case SiteCountry.nl:
      // Space between digits and letters is optional (#222); SA/SD/SS are not issued.
      return /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i.test((val || '').trim())
    case SiteCountry.be:
    case SiteCountry.dk:
      return /^[1-9]{1}[0-9]{3}$/i.test(val)
    case SiteCountry.uk:
      return /^([A-PR-UWYZ0-9][A-HK-Y0-9][AEHMNPRTVXY0-9]?[ABEHMNPRVWXY0-9]? {1,2}[0-9][ABD-HJLN-UW-Z]{2}|GIR 0AA)$/i.test(
        val,
      ) // tslint:disable-line
    case SiteCountry.ie:
      return /^[AC-FHKNPRTV-Y]{1}[0-9]{1}[0-9W]{1}[ \-]?[0-9AC-FHKNPRTV-Y]{4}$/i.test(
        val,
      )
    default:
      return true
  }
}

// The engine's province fallback splits contact_zipcode on the space
// (srprsme_engine/utils/city.py), so NL is always sent as "NNNN LL".
export function normalizePostcode(
  val: string | undefined,
  country: SiteCountry,
): string | undefined {
  if (!val || country !== SiteCountry.nl) {
    return val
  }

  const compact = val.replace(/\s/g, '').toUpperCase()
  if (!/^[1-9][0-9]{3}[A-Z]{2}$/.test(compact)) {
    return val
  }

  return `${compact.slice(0, 4)} ${compact.slice(4)}`
}

export function validPhone(val: string, country: SiteCountry) {
  switch (country) {
    case SiteCountry.nl:
      return /^(0|\+31)([1-5,6,7-8]\d{8})$/i.test(val?.replace(/ /g, ``))
    default:
      return true
  }
}

export const validatorFunctions: {
  [key: string]: (val: any) => (val?: string) => boolean
} = {
  hasMinLength: (length: number) => {
    return (val?: string) => (val ? val.length >= length : true)
  },
  hasMinLetters: (length: number) => {
    return (val?: string) =>
      val
        ? val.split('').filter(x => /([A-Za-z])/i.test(x)).length >= length
        : true
  },
  hasMinNumbers: (length: number) => {
    return (val?: string) =>
      val
        ? val.split('').filter(x => /([0-9])/i.test(x)).length >= length
        : true
  },
  hasMaxChar: (props: { char: string; length: number }) => {
    return (val?: string) =>
      val
        ? val.split('').filter(x => x === props.char).length <= props.length
        : true
  },
  hasMaxLength: (length: number) => {
    return (val?: string) => (val ? val.length <= length : true)
  },
  hasMaxNumbers: (length: number) => {
    return (val?: string) =>
      val
        ? val.split('').filter(x => /([0-9])/i.test(x)).length <= length
        : true
  },
  noChars: (chars: string) => {
    return (val?: string) =>
      val ? val.split('').filter(x => chars.includes(x)).length === 0 : true
  },
}

export const validators: { [key: string]: (val?: string) => boolean } = {
  requiredString: (val?: string) => Boolean(val && val.length),
  isEmail: (val?: string) =>
    val
      ? /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(
          val,
        ) // tslint:disable-line
      : true,
  isTel: (val?: string) =>
    val
      ? // tslint:disable-next-line:max-line-length
        /(^\+[0-9]{2}|^\+[0-9]{2}\(0\)|^\(\+[0-9]{2}\)\(0\)|^00[0-9]{2}|^0)([\s]{1}|)([0-9]{9}$|[0-9\-\s]{10}$|[0-9]{10}|[0-9\-\s]{11})/i.test(
          val,
        ) ||
        // tslint:disable-next-line:max-line-length
        /(^\+[0-9]{2}|^\(\+[0-9]{2}\|^00[0-9]{2}|)([\s\-]{1}|)(0[0-9]{1}|\(0[0-9]{1}\))([\s\-]{1}|)([0-9\-\s]{8}$)/i.test(
          val,
        )
      : true,
  cardOrder: (cardOrder: any) => {
    return (
      cardOrder.$form.valid &&
      (cardOrder.ExpirationMonth ? cardOrder.ExpirationMonth.valid : false) &&
      (cardOrder.ExpirationYear ? cardOrder.ExpirationYear.valid : false)
    )
  },
  futureDate: (val?: string) => (val ? new Date(val) < new Date() : true),
  maxAge: (val?: string) =>
    val ? new Date(val) > new Date('1900/01/01') : true,
  noLetters: (val?: string) => (val ? /(^([^A-Za-z]+)$)/i.test(val) : true),
  noNumbers: (val?: string) => (val ? /(^([^0-9]+)$)/i.test(val) : true),
}
